import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { NavLinks } from "../components/NavLinks";
import { NewProjectDialog } from "../components/NewProjectDialog";
import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { getMe, getProjects } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { formatDate, formatDateTime } from "../lib/format";

/** 封面：URL 失效时回退到首字占位，不留破图。 */
function ProjectCover({ title, coverUrl }: { title: string; coverUrl: string | null }) {
  const [failed, setFailed] = useState(false);
  if (coverUrl && !failed) {
    return (
      <img
        src={coverUrl}
        alt={`${title} 封面`}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={`${title} 封面占位`}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(145deg, var(--amber-soft), transparent 70%), var(--panel-2)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--sans)",
          fontSize: 40,
          color: "var(--amber)",
          opacity: 0.85,
        }}
      >
        {title.slice(0, 1)}
      </span>
    </div>
  );
}

/**
 * 项目列表：延续工作台同一套色板与字体（暖石墨 + 同族标题 + 等宽元数据），
 * 不另起一套视觉语言。
 */
export function ProjectsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: () => getProjects() });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: () => getMe() });

  const projects = projectsQuery.data?.items ?? [];
  const displayName = meQuery.data?.displayName ?? meQuery.data?.email ?? null;
  const initial = displayName ? displayName.slice(0, 1).toUpperCase() : "D";

  return (
    <div style={{ minHeight: "100dvh" }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          borderBottom: "1px solid var(--line)",
          background: "var(--bg-2)",
        }}
      >
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-4 px-6">
          {/* 品牌位：mark 回首页 + 静态 wordmark；跨站导航改成平铺一行（P1-W3/W4 第 3 条）。 */}
          <div className="dp-projects-brand">
            <Link to="/" aria-label="Drama Pie 首页" className="dp-projects-mark">
              <span className="dp-brand-mark" aria-hidden="true" style={{ fontSize: 16 }}>
                D
              </span>
            </Link>
            <span className="dp-projects-wordmark">Drama Pie</span>
            <NavLinks variant="topbar" />
          </div>

          <div className="ml-auto flex items-center gap-4">
            {displayName ? (
              <span className="hidden items-center gap-2 sm:flex">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 items-center justify-center rounded-full"
                  style={{
                    background: "var(--panel-2)",
                    border: "1px solid var(--line)",
                    fontFamily: "var(--mono)",
                    fontSize: 12,
                    color: "var(--ink-2)",
                  }}
                >
                  {initial}
                </span>
                <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-3)" }}>
                  {displayName}
                </span>
              </span>
            ) : null}
            <button type="button" onClick={() => setDialogOpen(true)} className="dp-btn primary">
              新建项目
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-12">
        <div style={{ marginBottom: 34 }}>
          <p className="dp-ep-eyebrow">Projects</p>
          <h1
            style={{
              margin: "10px 0 6px",
              fontFamily: "var(--sans)",
              fontSize: 34,
              fontWeight: 500,
              lineHeight: 1.15,
              letterSpacing: ".03em",
              color: "var(--ink)",
            }}
          >
            我的短剧
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
            每部短剧按集拆分，故事板按场次组织。
          </p>
        </div>

        {projectsQuery.isPending ? (
          <LoadingState label="正在加载项目…" />
        ) : projectsQuery.isError ? (
          <ErrorState
            message={errorMessage(projectsQuery.error, "项目列表加载失败")}
            onRetry={() => {
              void projectsQuery.refetch();
            }}
          />
        ) : projects.length === 0 ? (
          <EmptyState
            title="还没有项目"
            description="从一部短剧开始：定好标题、画幅和风格，第一集的故事板就会出现在工作台里。"
            action={
              <button type="button" onClick={() => setDialogOpen(true)} className="dp-btn primary">
                新建项目
              </button>
            }
          />
        ) : (
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <li key={project.id}>
                <Link
                  to={`/p/${project.id}`}
                  className="group flex h-full flex-col overflow-hidden transition-colors"
                  style={{
                    border: "1px solid var(--line)",
                    borderRadius: 10,
                    background:
                      "linear-gradient(180deg, var(--wash-1), transparent), var(--panel)",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      aspectRatio: "16 / 9",
                      width: "100%",
                      overflow: "hidden",
                      background: "var(--panel-2)",
                    }}
                  >
                    <ProjectCover title={project.title} coverUrl={project.coverUrl} />
                  </div>
                  <div className="flex flex-1 flex-col" style={{ padding: "13px 14px 14px" }}>
                    <h2
                      style={{
                        margin: 0,
                        fontFamily: "var(--sans)",
                        fontSize: 20,
                        fontWeight: 500,
                        letterSpacing: ".04em",
                        color: "var(--ink)",
                      }}
                    >
                      {project.title}
                    </h2>
                    <p
                      style={{
                        margin: "5px 0 0",
                        fontFamily: "var(--mono)",
                        fontSize: 12,
                        letterSpacing: ".04em",
                        color: "var(--ink-3)",
                      }}
                    >
                      {project.episodeCount} 集 · {project.shotCount} 镜
                    </p>
                    <p
                      style={{
                        marginTop: "auto",
                        paddingTop: 12,
                        fontFamily: "var(--mono)",
                        fontSize: 12,
                        color: "var(--ink-4)",
                      }}
                      title={formatDateTime(project.updatedAt)}
                    >
                      更新于 {formatDate(project.updatedAt)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>

      {dialogOpen ? <NewProjectDialog onClose={() => setDialogOpen(false)} /> : null}
    </div>
  );
}
